import React from 'react';
import { motion } from 'framer-motion';

interface GPUTemperatureProps {
  temperature: number;
  powerDraw: number;
}

const GPUTemperature: React.FC<GPUTemperatureProps> = ({ temperature, powerDraw }) => {
  // Temperature thresholds - more subtle colors
  const getTempColor = () => {
    if (temperature > 90) return '#ef4444'; // red - critical
    if (temperature > 80) return '#fbbf24'; // yellow - warning
    if (temperature > 70) return '#60a5fa'; // blue - normal high
    return '#10b981'; // green - cool
  };

  const tempColor = getTempColor();
  const tempPercentage = Math.min(temperature / 100, 1) * 100;

  return (
    <div className="bg-black/50 backdrop-blur-sm p-3 border border-gray-800/50 h-full flex flex-col shadow-lg">
      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Thermal</h3>
      
      <div className="flex-1 space-y-3">
        {/* Temperature */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Temp</span>
            <motion.span
              key={temperature}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-sm font-bold"
              style={{ color: tempColor }}
            >
              {temperature}°C
            </motion.span>
          </div>
          
          <div className="h-2 bg-gray-800/50 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${tempPercentage}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="h-full"
              style={{ 
                backgroundColor: tempColor,
                boxShadow: `0 0 10px ${tempColor}40`
              }}
            />
          </div>
          
          {temperature > 80 && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1"
            >
              <span className="text-xs text-amber-500">
                {temperature > 90 ? '⚠️ Critical' : '⚠️ High'}
              </span>
            </motion.div>
          )}
        </div>

        {/* Power Draw */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Power</span>
            <motion.span
              key={powerDraw}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-sm font-bold text-blue-400"
            >
              {powerDraw}W
            </motion.span>
          </div>
          
          <div className="h-2 bg-gray-800/50 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(powerDraw / 400, 1) * 100}%` }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-blue-500/60 to-blue-400/60"
              style={{ 
                boxShadow: `0 0 10px #3b82f640`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GPUTemperature;